/**
 * src/components/_markdownLite.js — the one markdown-lite renderer.
 *
 * ⛔⛔ EXTRACTED, NOT WRITTEN. This code was a closure inside AIReportPanel.js,
 * and that closure's own comment said it "mirrors the renderer in AdminAIPage"
 * — i.e. there were already TWO copies. P182 PR2 needed a third for the CEO
 * pipeline report, which is exactly how this repo got a parser bug fixed in a
 * helper and left alive in an inline duplicate before.
 *
 * So the third copy was not written: this module is the second one moved out,
 * and AIReportPanel now imports it.
 *
 * ⚠⚠ ADMINAIPAGE STILL CARRIES ITS OWN COPY, AND THE COPIES HAVE DIVERGED —
 * measured 31 Oct 2026, not inferred. AdminAIPage.js:93 renders `---` as an
 * <hr>; the extracted copy did not, so the surface the CEO reads was the
 * WEAKER of the two. That is the "extracted, behaviour unchanged" note above
 * being true and still leaving a gap: the extraction preserved the copy it
 * came from, not the better copy. Card P197 adds tables and <hr> HERE, which
 * fixes AiPipelineReport and AIReportPanel and leaves AdminAIPage as the one
 * surface that still renders a markdown table as raw pipes. Unifying it is a
 * separate change to a page this card does not touch — named, with a line
 * number, so the next person has a fact rather than a grep.
 *
 * ── Card P197 STEP 0 (Daniel, 31 Oct, seen live) ────────────────────────────
 * The CEO report reads as plain text. The model emits GFM tables and `---`
 * rules; neither was supported, so 18 table lines rendered as one <p> each of
 * raw "| Metric | Value |" and six rules rendered as the literal text "---".
 *
 * ⭐⭐ ONE PARSE, TWO EMITTERS. The card requires that "Copy report / Print
 * must use the same rendering". Print already shows the DOM, but COPY used to
 * hand over the model's raw markdown — so the moment the screen gained real
 * tables, the clipboard would have kept the pipes. A second text-renderer
 * would be a second parser, and this file exists precisely because this repo
 * has fixed a parser bug in one copy and left it alive in another. So:
 * parseMarkdownLite() classifies lines ONCE into blocks, and the React
 * emitter and the plain-text emitter both consume that block list. Adding a
 * block type to one emitter and not the other is a test failure, not a bug
 * report (see markdownLiteRendering.test.js, "both emitters handle every
 * block type the parser can produce").
 *
 * ⛔ NO RAW HTML FROM THE MODEL, EVER. Every emitted string below is a React
 * text child, so React escapes it. React's raw-HTML escape hatch — the prop
 * that takes an { __html } object — is absent from this file and must stay
 * absent: the input is model output, and the model is quoting a database. A
 * red arm in the test file proves a <script> in the report is rendered as
 * visible text rather than executed.
 *
 * ⚠⚠ THAT PROP IS DESCRIBED HERE RATHER THAN NAMED, AND THE REASON IS A
 * DEFECT IN THE GUARD THAT WATCHES IT. tests/unit/security/xss.test.js greps
 * src/ for the literal identifier WITHOUT STRIPPING COMMENTS, so this
 * paragraph — written to forbid the thing — was itself reported as an
 * "unwhitelisted usage" (measured 31 Oct 2026, card P197). The options were
 * to tag a comment with an XSS-ALLOWLIST marker, which trains the next reader
 * that prose can carry a security exemption, or to describe the API instead.
 * ⛔ DO NOT "FIX" THIS BY ADDING THE ALLOWLIST COMMENT. The guard should
 * strip comments the way markdownLiteRendering.test.js does for this exact
 * reason; that is a change to a security control and belongs in its own PR.
 *
 * Supports: #-headers, - and * bullets, 1. ordered lists, **bold**, _italic_,
 * `inline code`, GFM tables, --- rules, and blank-line spacing.
 * Deliberately not a markdown library.
 */

import React from 'react';

/* ── Numeric detection, for right-aligning number columns ──────────────────
 * A money/percent/count cell as the model writes it: $1.44M, 28%, +12%,
 * 1,234, -5, 3.2x. The leading sign may be an ASCII hyphen or a real minus.
 */
const NUMERIC_CELL = /^[+\-−]?\s*[$€£]?\s*\d[\d,]*(\.\d+)?\s*([%x]|[KMB]\b)?$/i;

/*
 * ⚠ A column of numbers with one "not tracked" cell is still a number column.
 * The payload's own convention is that null+tracked:false must read as "not
 * tracked", never "0" — so those cells reach the table as a dash or as the
 * words, and a strict every()-is-numeric test would left-align the whole
 * column because of them. These tokens are IGNORED rather than counted
 * against the column; a column must still contain at least one real number.
 */
const BLANK_TOKENS = new Set(['', '-', '—', '–', 'n/a', 'na', 'null', 'not tracked', 'untracked']);

function isNumericColumn(cells) {
  let seenNumber = false;
  for (const raw of cells) {
    const c = String(raw == null ? '' : raw).trim();
    if (BLANK_TOKENS.has(c.toLowerCase())) continue;
    if (!NUMERIC_CELL.test(c)) return false;
    seenNumber = true;
  }
  return seenNumber;
}

/* A table row line: starts and ends with a pipe once trimmed. */
function isTableRow(line) {
  const t = line.trim();
  return t.startsWith('|') && t.endsWith('|') && t.length > 2;
}

/*
 * The GFM delimiter row: | --- | :--: | ---: |. ⚠ This is checked BEFORE the
 * `---` horizontal-rule branch, or "|---|---|" becomes a rule and the table
 * loses its header.
 */
function isDelimiterRow(line) {
  if (!isTableRow(line)) return false;
  return splitRow(line).every((c) => /^:?-{1,}:?$/.test(c.trim()) && c.trim().length > 0);
}

/* Split "| a | b |" into ['a','b'], honouring an escaped \| inside a cell. */
function splitRow(line) {
  const t = line.trim().replace(/^\|/, '').replace(/\|$/, '');
  const out = [];
  let cur = '';
  for (let i = 0; i < t.length; i++) {
    const ch = t[i];
    if (ch === '\\' && t[i + 1] === '|') { cur += '|'; i++; continue; }
    if (ch === '|') { out.push(cur.trim()); cur = ''; continue; }
    cur += ch;
  }
  out.push(cur.trim());
  return out;
}

/* Alignment from the delimiter row: :--- left, ---: right, :---: center. */
function alignmentOf(cell) {
  const t = cell.trim();
  const left = t.startsWith(':');
  const right = t.endsWith(':');
  if (left && right) return 'center';
  if (right) return 'right';
  if (left) return 'left';
  return null; // not declared — fall back to numeric detection
}

/**
 * parseMarkdownLite — text → an ordered list of blocks.
 *
 * Block types: heading, ul, ol, table, hr, para, spacer. Both emitters below
 * switch on exactly this set.
 */
export function parseMarkdownLite(text) {
  if (!text) return [];
  const lines = String(text).split('\n');
  const blocks = [];
  let listBuffer = [];
  let listType = null;

  function flushList() {
    if (listBuffer.length === 0) return;
    blocks.push({ type: listType === 'ol' ? 'ol' : 'ul', items: listBuffer });
    listBuffer = [];
    listType = null;
  }

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const trimmed = line.trim();

    // ── GFM table: a row followed by a delimiter row. Checked first. ──────
    if (isTableRow(line) && i + 1 < lines.length && isDelimiterRow(lines[i + 1])) {
      flushList();
      const headers = splitRow(line);
      const declared = splitRow(lines[i + 1]).map(alignmentOf);
      const rows = [];
      let j = i + 2;
      while (j < lines.length && isTableRow(lines[j]) && !isDelimiterRow(lines[j])) {
        const cells = splitRow(lines[j]);
        /*
         * ⚠ RAGGED ROWS ARE PADDED, NOT DROPPED. A model that emits one cell
         * too few would otherwise lose a whole row of real figures silently,
         * and a missing row in a financial brief is worse than a blank cell.
         */
        while (cells.length < headers.length) cells.push('');
        rows.push(cells.slice(0, headers.length));
        j++;
      }
      const align = headers.map((_, c) => {
        if (declared[c]) return declared[c];
        return isNumericColumn(rows.map((r) => r[c])) ? 'right' : 'left';
      });
      blocks.push({ type: 'table', headers, rows, align });
      i = j - 1;
      continue;
    }

    if (/^#{1,6}\s/.test(trimmed)) {
      flushList();
      blocks.push({ type: 'heading', level: trimmed.match(/^#+/)[0].length, text: trimmed.replace(/^#+\s/, '') });
    } else if (/^(-{3,}|\*{3,}|_{3,})$/.test(trimmed)) {
      /*
       * ⚠ A `---` directly under a paragraph is a setext H2 in real GFM. It
       * is a RULE here, deliberately: AdminAIPage.js:93 has always rendered it
       * that way, and what the CEO report actually contains is six separators
       * between sections (measured live, 31 Oct). Promoting them to headings
       * would re-title six sections with the last line of the section above.
       */
      flushList();
      blocks.push({ type: 'hr' });
    } else if (/^[-*]\s/.test(trimmed)) {
      if (listType !== 'ul') flushList();
      listType = 'ul';
      listBuffer.push(trimmed.replace(/^[-*]\s/, ''));
    } else if (/^\d+\.\s/.test(trimmed)) {
      if (listType !== 'ol') flushList();
      listType = 'ol';
      listBuffer.push(trimmed.replace(/^\d+\.\s/, ''));
    } else if (trimmed === '') {
      flushList();
      blocks.push({ type: 'spacer' });
    } else {
      flushList();
      blocks.push({ type: 'para', text: line });
    }
  }
  flushList();
  return blocks;
}

/* ── Inline spans, shared by both emitters ──────────────────────────────── */
const INLINE = /(\*\*(.+?)\*\*|`(.+?)`|_(.+?)_)/g;

function formatInline(str) {
  const parts = [];
  let lastIndex = 0;
  const regex = new RegExp(INLINE.source, 'g');
  let match, key = 0;
  while ((match = regex.exec(str)) !== null) {
    if (match.index > lastIndex) parts.push(str.substring(lastIndex, match.index));
    if (match[2]) parts.push(<strong key={key++}>{match[2]}</strong>);
    else if (match[3]) parts.push(<code key={key++} style={{ background: '#f0f0f0', padding: '1px 4px', borderRadius: 3, fontSize: '0.9em' }}>{match[3]}</code>);
    else if (match[4]) parts.push(<em key={key++}>{match[4]}</em>);
    lastIndex = regex.lastIndex;
  }
  if (lastIndex < str.length) parts.push(str.substring(lastIndex));
  return parts.length > 0 ? parts : str;
}

/** Same spans, flattened to text — for the clipboard. */
function inlineToText(str) {
  return String(str == null ? '' : str).replace(new RegExp(INLINE.source, 'g'),
    (m, _all, bold, code, italic) => bold || code || italic || m);
}

const TH = {
  textAlign: 'left', padding: '6px 10px', borderBottom: '2px solid #d1d5db',
  fontWeight: 700, fontSize: 12, color: '#374151', whiteSpace: 'nowrap',
};
const TD = { padding: '5px 10px', borderBottom: '1px solid #f1f5f9', fontSize: 13 };

/**
 * renderMarkdownLite — the React emitter.
 *
 * ⚠ Returns an ARRAY of elements, as it always has; the two callers drop it
 * straight into an <article>. Changing that to a wrapper element would be a
 * layout change in AIReportPanel, which this card does not touch.
 */
export function renderMarkdownLite(text) {
  if (!text) return null;
  const blocks = parseMarkdownLite(text);
  if (blocks.length === 0) return null;

  return blocks.map((b, i) => {
    switch (b.type) {
      case 'heading': {
        const Tag = `h${Math.min(b.level + 2, 6)}`;
        return React.createElement(Tag, { key: `h-${i}`, style: { margin: '14px 0 6px', color: '#1f2937' } }, formatInline(b.text));
      }
      case 'ul':
      case 'ol': {
        const Tag = b.type;
        return (
          <Tag key={`list-${i}`} style={{ margin: '8px 0', paddingLeft: 22 }}>
            {b.items.map((item, k) => <li key={k} style={{ marginBottom: 4 }}>{formatInline(item)}</li>)}
          </Tag>
        );
      }
      case 'table':
        return (
          /*
           * ⚠ .responsive-scroll + an explicit minWidth is the repo's own
           * convention for a wide table on a phone (CLAUDE.md, Frontend
           * styling) — native momentum scroll rather than clipping. Writing a
           * bespoke overflow wrapper here would be a fourth way to do it.
           */
          <div key={`t-${i}`} className="responsive-scroll" style={{ margin: '10px 0' }}>
            <table
              data-testid="md-table"
              style={{ minWidth: 360, width: '100%', borderCollapse: 'collapse' }}
            >
              <thead>
                <tr>
                  {b.headers.map((h, c) => (
                    <th key={c} scope="col" style={{ ...TH, textAlign: b.align[c] }}>{formatInline(h)}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {b.rows.map((row, r) => (
                  /* Zebra rows. ⚠ The print stylesheet re-states this with
                     -webkit-print-color-adjust, or the banding vanishes in a
                     PDF — a background colour is the first thing a print
                     pipeline drops. */
                  <tr key={r} style={{ background: r % 2 ? '#f9fafb' : 'transparent' }}>
                    {row.map((cell, c) => (
                      <td key={c} style={{ ...TD, textAlign: b.align[c],
                        fontVariantNumeric: b.align[c] === 'right' ? 'tabular-nums' : 'normal' }}>
                        {formatInline(cell)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      case 'hr':
        return <hr key={`hr-${i}`} style={{ margin: '12px 0', border: 'none', borderTop: '1px solid #e5e7eb' }} />;
      case 'spacer':
        return <div key={`br-${i}`} style={{ height: 4 }} />;
      case 'para':
      default:
        return <p key={`p-${i}`} style={{ margin: '4px 0' }}>{formatInline(b.text)}</p>;
    }
  });
}

/**
 * markdownLiteToText — the plain-text emitter, for "Copy report".
 *
 * ⛔⛔ IT HAS NO CALLER TODAY, AND THAT IS WRITTEN HERE RATHER THAN LEFT TO BE
 * DISCOVERED. It was built for the CEO report's Copy button; #780 removed
 * that surface's prose path entirely and #784 confirmed structured-only, so
 * the CEO export now goes through ceoReportText.js instead. The surviving
 * prose surface, AIReportPanel.js (billing), has no Copy, Print or clipboard
 * affordance at all.
 *
 * ⚠⚠ THIS REPO HAS A STANDING RULE ABOUT EXACTLY THIS SHAPE: a granted
 * capability with no caller is a paragraph, not a control. It is kept on
 * Cowork's explicit instruction (P216) because it is a tested pure function
 * and because it holds up the one-parse/two-emitters guard below — but it
 * should not stay uncalled indefinitely. EITHER wire a Copy button into
 * AIReportPanel (a product change, its own card) OR delete this function and
 * the two arms that pin it. Keeping it unused and unmentioned is the option
 * that ends with someone finding dead code and guessing why.
 *
 * ⛔ This exists so the clipboard shows what the screen shows. Copying the
 * model's raw source instead would put "|---|---|" back in front of whoever
 * pastes the brief into an email — the exact complaint this card is fixing,
 * displaced one surface sideways.
 */
export function markdownLiteToText(text) {
  if (!text) return '';
  const out = [];
  for (const b of parseMarkdownLite(text)) {
    switch (b.type) {
      case 'heading':
        out.push(inlineToText(b.text));
        break;
      case 'ul':
        b.items.forEach((it) => out.push(`  • ${inlineToText(it)}`));
        break;
      case 'ol':
        b.items.forEach((it, k) => out.push(`  ${k + 1}. ${inlineToText(it)}`));
        break;
      case 'table': {
        // Column widths from the widest cell, so the columns line up in a
        // monospaced paste; number columns are right-aligned as on screen.
        const all = [b.headers, ...b.rows].map((r) => r.map(inlineToText));
        const w = b.headers.map((_, c) => Math.max(...all.map((r) => (r[c] || '').length)));
        const pad = (s, c) => (b.align[c] === 'right' ? String(s).padStart(w[c]) : String(s).padEnd(w[c]));
        out.push(all[0].map(pad).join('  ').trimEnd());
        out.push(w.map((n) => '-'.repeat(n)).join('  '));
        all.slice(1).forEach((r) => out.push(r.map(pad).join('  ').trimEnd()));
        break;
      }
      case 'hr':
        out.push('─'.repeat(40));
        break;
      case 'spacer':
        out.push('');
        break;
      case 'para':
      default:
        out.push(inlineToText(b.text));
        break;
    }
  }
  return out.join('\n').replace(/\n{3,}/g, '\n\n').trim();
}
